import {queryOptions, type QueryClient} from "@tanstack/react-query";
import {z as zod} from "zod";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {countryValidator, type Country} from "@vesta/vos-types/modules/platformLevel/geography/domain/Country";
import type {Notification, NotificationStatus} from "@vesta/vos-types/modules/platformLevel/notifications/domain/Notification";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {
    organizationalUnitTemplateValidator,
    type OrganizationalUnitTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {platformAccessMatrixValidator, type PlatformAccessMatrix} from "@vesta/vos-types/modules/platformLevel/authorization/domain/PlatformAccessMatrix";
import type {TimeZone} from "@vesta/vos-types/modules/platformLevel/timeZones/domain/TimeZone";
import {
    domainAggregateTemplateValidator,
    type DomainAggregateTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/domainAggregateWorkspaceCapability/domain/DomainAggregateTemplate";
import {userDetailsValidator, type UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {userValidator as userZodSchema} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {
    userTemplateValidator,
    type UserTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {
    productCatalogueTemplateValidator,
    type ProductCatalogueTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogueTemplate";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import {notificationValidator as notificationZodValidator} from "@vesta/vos-types/modules/platformLevel/notifications/domain/Notification";
import {organizationalUnitValidator as organizationalUnitZodSchema} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {productCatalogueValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import {
    productInventoryValidator,
    type ProductInventory,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productInventoryWorkspaceCapability/domain/ProductInventory";
import {workspaceValidator, type Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import {
    invitedWorkspaceValidator,
    type InvitedWorkspace,
} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";
import {authenticatedUserAuthMethodZodValidator} from "@vesta/vos-types/modules/platformLevel/authentication/domain/AuthenticatedUserAuthMethod";
import type {HeimdallUser} from "~/routes/_withAuthenticationStatus/-types";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {integerZodValidator, jsonZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    ADGPT__CHATS__TITLE__QUERY_KEY,
    COUNTRIES__QUERY_KEY,
    TIME_ZONES__QUERY_KEY,
    USER__ACCESSIBLE_WORKSPACES__QUERY_KEY,
    USER__HEIMDALL_USER__QUERY_KEY,
    USER__PLATFORM_ACCESS_MATRIX__QUERY_KEY,
    USER__PROFILE_DETAILS__QUERY_KEY,
    USER__WORKSPACE_ACCESS_MATRIX__QUERY_KEY,
    USER__WORKSPACE_CAPABILITY_ACCESS_MATRIX__QUERY_KEY,
    WORKSPACE__ORGANIZATION_UNIT_TEMPLATES__QUERY_KEY,
    WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY,
    WORKSPACE__USER_TEMPLATES__QUERY_KEY,
    WORKSPACE__USERS__QUERY_KEY,
    WORKSPACE__PRODUCT_CATALOGUE_TEMPLATES__QUERY_KEY,
    WORKSPACE__PRODUCT_CATALOGUES__QUERY_KEY,
    WORKSPACE__PRODUCT_INVENTORIES__QUERY_KEY,
    WORKSPACE__CAPABILITIES__QUERY_KEY,
    WORKSPACE__UI_CONFIGS__QUERY_KEY,
    WORKSPACE__INVITES__QUERY_KEY,
    WORKSPACE__INVITES__COUNT__QUERY_KEY,
    WORKSPACE__INVITES__COLUMN__VALUES__QUERY_KEY,
    WORKSPACE__INVITES__DATA__QUERY_KEY,
    WORKSPACE__USERS__COLUMN_VALUES__QUERY_KEY,
    WORKSPACE__DOMAIN_AGGREGATE_TEMPLATES__QUERY_KEY,
    WORKSPACE__USER_DEFINED_OBJECT_TEMPLATES__QUERY_KEY,
    WORKSPACE__USER_DEFINED_OBJECTS__QUERY_KEY,
    WORKSPACE__USERS__PLATFORM_DETAILS__QUERY_KEY,
    WORKSPACE__COMBINED_USERS__QUERY_KEY,
} from "~/utilities/queryKeys";
import {workspaceUiConfigValidator, WorkspaceUiConfigStatus, type WorkspaceUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfig";
import {
    workspaceCapabilitiesValidator,
    type WorkspaceCapability,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {
    workspaceCapabilityAccessMatrixValidator,
    type WorkspaceCapabilityAccessMatrix,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityAccessMatrix";
import {workspaceAccessMatrixValidator, type WorkspaceAccessMatrix} from "@vesta/vos-types/modules/workspaceLevel/workspaceAccess/domain/WorkspaceAccessMatrix";
import {
    PrivateInviteStatus,
    privateInviteByInviteCodeValidator,
    privateInviteValidator,
    type PrivateInvite,
    type PrivateInviteByInviteCode,
} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";
import type {UsersTemplateSearchGroup} from "@vesta/vos-types/modules/workspaceLevel/users/domain/WorkspaceUsersConfiguration";
import type {CombinedUser} from "~/typeDefinitions";
import {buildPlutoCacheableGetUrl} from "~/utilities/plutoCacheableGetUrl";
import {
    userDefinedObjectTemplateValidator,
    type UserDefinedObjectTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import {
    userDefinedObjectValidator,
    type UserDefinedObject,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObject";

export const DATA_VIEWER_NULL_SENTINEL: Uuid = "00000000-0000-4000-a000-000000000000" as Uuid;
export const CACHEABLE_WORKSPACE_ITEM_FULL_READ_LIMIT = 99999;

export type PlutoComplexFilter = {
    filters: Array<PlutoComplexFilter | PlutoFilter>;
    logic: "AND" | "OR";
};

export type PlutoFilter = {
    column: string;
    operator: string;
    value: unknown;
};

export type PlutoOrderByItem = {
    column: string;
    direction: "asc" | "desc";
};

export type CacheableWorkspaceItemGetAllInput = {
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
};

export type CacheableWorkspaceItemCountAllInput = {
    complexFilters: PlutoComplexFilter;
};

export const EMPTY_CACHEABLE_WORKSPACE_ITEM_COMPLEX_FILTER: PlutoComplexFilter = {
    filters: [],
    logic: "AND",
};

export const CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT: CacheableWorkspaceItemGetAllInput = {
    complexFilters: EMPTY_CACHEABLE_WORKSPACE_ITEM_COMPLEX_FILTER,
    limit: CACHEABLE_WORKSPACE_ITEM_FULL_READ_LIMIT,
    offset: 0,
    orderBy: [],
};

// Single source of truth for the AdGPT sidebar chat list. Shared by the `_adgptScaffold` loader
// (ensureQueryData) and the `ConversationLayoutComponent` sidebar (useQuery) so their fetch + zod
// schema can never drift apart. `agentVersion` MUST stay in the schema: the sidebar routes v2 chats
// to the `adgpt2next` fork based on it, and zod strips any field not declared here — a previous
// loader/component schema mismatch silently dropped `agentVersion` and routed v2 chats to v1.
export function adgptChatsQueryOptions({
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    projectId = null,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    projectId?: string | null;
}) {
    const queryKey =
        projectId === null
            ? ([ADGPT__CHATS__TITLE__QUERY_KEY, workspaceId] as const)
            : ([ADGPT__CHATS__TITLE__QUERY_KEY, workspaceId, projectId] as const);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const allChatsResult = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/adgpt2/materialized/threads/search`,
                {
                    body: JSON.stringify({limit: 50, projectId, workspaceId}),
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                },
                zod.array(
                    zod.object({
                        id: uuidZodValidator,
                        title: zod.string().nullable(),
                        agentVersion: zod.string().nullable(),
                        // The project a v2 chat belongs to (SOW#9), or null for a loose chat. zod strips
                        // undeclared fields, so this must stay for project grouping to work.
                        projectId: zod.string().nullable().optional(),
                    }),
                ),
            );

            if (allChatsResult.success !== true) {
                throw allChatsResult.error;
            }
            return allChatsResult.data;
        },
        staleTime: Infinity,
    });
}

function normalizeUsersColumnId(columnId: string): string {
    return columnId;
}

function filterOperationToPlutoOperator(operation: FilterOperation): string {
    switch (operation) {
        case FilterOperation.contains: {
            return "contains";
        }
        case FilterOperation.equals: {
            return "equals";
        }
        case FilterOperation.endsWith: {
            return "endsWith";
        }
        case FilterOperation.greaterThan: {
            return "greaterThan";
        }
        case FilterOperation.greaterThanEqualTo: {
            return "greaterThanOrEqualTo";
        }
        case FilterOperation.in: {
            return "in";
        }
        case FilterOperation.lessThan: {
            return "lessThan";
        }
        case FilterOperation.lessThanEqualTo: {
            return "lessThanOrEqualTo";
        }
        case FilterOperation.notContains: {
            return "notContains";
        }
        case FilterOperation.notEquals: {
            return "notEquals";
        }
        case FilterOperation.startsWith: {
            return "startsWith";
        }
        case FilterOperation.isNull: {
            return "isnull";
        }
        case FilterOperation.isNotNull: {
            return "isnotnull";
        }
    }
}

function sortDirectionToPlutoDirection(direction: SortDirection): PlutoOrderByItem["direction"] {
    return direction === SortDirection.descending ? "desc" : "asc";
}

function lineItemToPlutoFilter(lineItem: FilterConfigurationLineItem, normalizeColumnId: (columnId: string) => string): PlutoFilter | PlutoComplexFilter {
    const column = normalizeColumnId(lineItem.columnId);
    if (lineItem.operation === FilterOperation.isNull || lineItem.operation === FilterOperation.isNotNull) {
        return {
            column: column,
            operator: filterOperationToPlutoOperator(lineItem.operation),
            value: null,
        };
    }
    if (lineItem.operation !== FilterOperation.in) {
        return {
            column: column,
            operator: filterOperationToPlutoOperator(lineItem.operation),
            value: lineItem.value,
        };
    }

    const nonNullValues = lineItem.values.filter((value) => value !== DATA_VIEWER_NULL_SENTINEL);
    const hasNullSentinel = nonNullValues.length !== lineItem.values.length;
    const nullFilter: PlutoFilter = {
        column: column,
        operator: filterOperationToPlutoOperator(FilterOperation.equals),
        value: null,
    };

    if (hasNullSentinel === false) {
        return {
            column: column,
            operator: filterOperationToPlutoOperator(FilterOperation.in),
            value: lineItem.values,
        };
    }
    if (nonNullValues.length === 0) {
        return nullFilter;
    }
    return {
        logic: "OR",
        filters: [
            {
                column: column,
                operator: filterOperationToPlutoOperator(FilterOperation.in),
                value: nonNullValues,
            },
            nullFilter,
        ],
    };
}

function searchConfigurationToPlutoFilter(
    searchConfiguration: SearchConfiguration | undefined,
    normalizeColumnId: (columnId: string) => string,
): PlutoComplexFilter | null {
    if (searchConfiguration === undefined || searchConfiguration.searchColumns.length === 0 || searchConfiguration.searchValue.trim() === "") {
        return null;
    }

    return {
        logic: "OR",
        filters: searchConfiguration.searchColumns.map((columnId) => ({
            column: normalizeColumnId(columnId),
            operator: filterOperationToPlutoOperator(FilterOperation.contains),
            value: searchConfiguration.searchValue,
        })),
    };
}

function templateSearchGroupsToPlutoFilter(
    searchConfiguration: SearchConfiguration | undefined,
    templateSearchGroups: Array<UsersTemplateSearchGroup> | undefined,
    normalizeColumnId: (columnId: string) => string,
): PlutoComplexFilter | null {
    if (
        searchConfiguration === undefined ||
        searchConfiguration.searchValue.trim() === "" ||
        templateSearchGroups === undefined ||
        templateSearchGroups.length === 0
    ) {
        return null;
    }

    const filters: Array<PlutoComplexFilter> = [];
    for (const templateSearchGroup of templateSearchGroups) {
        if (templateSearchGroup.userTemplateIds.length === 0 || templateSearchGroup.searchColumns.length === 0) {
            continue;
        }

        filters.push({
            logic: "AND",
            filters: [
                {
                    column: "templateId",
                    operator: filterOperationToPlutoOperator(FilterOperation.in),
                    value: templateSearchGroup.userTemplateIds,
                },
                {
                    logic: "OR",
                    filters: templateSearchGroup.searchColumns.map((columnId) => ({
                        column: normalizeColumnId(columnId),
                        operator: filterOperationToPlutoOperator(FilterOperation.contains),
                        value: searchConfiguration.searchValue,
                    })),
                },
            ],
        });
    }

    if (filters.length === 0) {
        return null;
    }

    return {
        logic: "OR",
        filters: filters,
    };
}

export function convertOttopilotViewerFiltersToPlutoComplexFilter({
    filterConfiguration,
    searchConfiguration,
    templateSearchGroups,
    normalizeColumnId = normalizeUsersColumnId,
}: {
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    templateSearchGroups?: Array<UsersTemplateSearchGroup>;
    normalizeColumnId?: (columnId: string) => string;
}): PlutoComplexFilter {
    const templateSearchFilter = templateSearchGroupsToPlutoFilter(searchConfiguration, templateSearchGroups, normalizeColumnId);
    const flatSearchFilter = templateSearchFilter === null ? searchConfigurationToPlutoFilter(searchConfiguration, normalizeColumnId) : null;

    return {
        logic: "AND",
        filters: [
            ...filterConfiguration.lineItems.map((lineItem) => lineItemToPlutoFilter(lineItem, normalizeColumnId)),
            ...(flatSearchFilter === null ? [] : [flatSearchFilter]),
            ...(templateSearchFilter === null ? [] : [templateSearchFilter]),
        ],
    };
}

export function convertOttopilotViewerSortToPlutoOrderBy(
    sortConfiguration: SortConfiguration,
    normalizeColumnId: (columnId: string) => string = normalizeUsersColumnId,
): Array<PlutoOrderByItem> {
    return sortConfiguration.lineItems.map((lineItem) => ({
        column: normalizeColumnId(lineItem.columnId),
        direction: sortDirectionToPlutoDirection(lineItem.direction),
    }));
}

export async function getHeimdallUserWithCaching({
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    //
}): Promise<Result<HeimdallUser | null>> {
    const heimdallUserResult = await queryClient.ensureQueryData({
        queryKey: [USER__HEIMDALL_USER__QUERY_KEY, accessToken],
        queryFn: async () => {
            const heimdallUserResponse = await fetch(`${intellsysBaseUrl}/oauth/userinfo`, {
                method: "GET",
                credentials: "include",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const heimdallUserResponseBodyRaw = await heimdallUserResponse.text();
            if (heimdallUserResponse.ok !== true) {
                // Currently invalid/expired tokens will also throw an error, and getting the exact reason
                // for the failure is slightly tricky. So, trying to get the cause of failure (backend not
                // responding/invalid response format/invalid token) by checking error message - which
                // is definitely an imperfect solution.
                // Typically we add `Error communicating with backend` when we detect a failure.
                // That means when the upstream throws an error (eg: for invalid token), it will
                // probably not include that phrase in the response. So, use that as a discriminant.
                throw new Error("Error communicating with backend", {cause: heimdallUserResponseBodyRaw});
            }
            const heimdallUserResult = jsonZodValidator
                .pipe(
                    zod.object({
                        // TODO: Remove the catch later
                        authMethods: zod.array(authenticatedUserAuthMethodZodValidator).catch([]),
                        sub: uuidZodValidator,
                    }),
                )
                .safeParse(heimdallUserResponseBodyRaw);
            if (heimdallUserResult.success === false) {
                return dataResult(null);
            }

            return dataResult({
                accessToken: accessToken,
                authMethods: heimdallUserResult.data.authMethods,
                id: heimdallUserResult.data.sub,
            });
        },
        retry: false,
        staleTime: Infinity,
    });
    return heimdallUserResult;
}

export async function getUserProfileDetailsWithCaching({
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    //
}): Promise<Result<UserDetails | null>> {
    const userProfileDetailsResult = await queryClient.ensureQueryData({
        queryKey: [USER__PROFILE_DETAILS__QUERY_KEY, accessToken],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/user-details/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({}),
                },
                userDetailsValidator.nullable(),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return userProfileDetailsResult;
}

export async function getAccessibleWorkspacesWithCaching({
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<Workspace[]>> {
    const accessibleWorkspacesResult = await queryClient.ensureQueryData({
        queryKey: [USER__ACCESSIBLE_WORKSPACES__QUERY_KEY, accessToken],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/workspaces/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({}),
                },
                zod.array(workspaceValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return accessibleWorkspacesResult;
}

// TODO: Fix type
export async function getInvitesWorkspacesWithCaching({
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<Array<InvitedWorkspace>>> {
    const accessibleWorkspacesResult = await queryClient.ensureQueryData({
        queryKey: [USER__ACCESSIBLE_WORKSPACES__QUERY_KEY, "invited", accessToken],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/workspaces/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({}),
                },
                zod.array(invitedWorkspaceValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return accessibleWorkspacesResult;
}

export async function getEnabledWorkspaceUiConfigWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<WorkspaceUiConfig | null>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__UI_CONFIGS__QUERY_KEY, workspaceId],
        queryFn: async () => {
            const result = await robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({
                    input: {workspaceId: workspaceId},
                    path: "/workspace-ui-configs/get",
                    intellsysBaseUrl: intellsysBaseUrl,
                }),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                },
                zod.array(workspaceUiConfigValidator),
            );
            if (result.success !== true) {
                return result;
            }
            return dataResult(result.data.find((config) => config.status === WorkspaceUiConfigStatus.enabled) ?? null);
        },
        retry: false,
        staleTime: Infinity,
    });
}

export async function getCountriesWithCaching({
    queryClient,
    intellsysBaseUrl,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    //
}): Promise<Result<Array<Country>>> {
    const countriesResult = await queryClient.ensureQueryData({
        queryKey: [COUNTRIES__QUERY_KEY],
        queryFn: () =>
            robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({
                    input: {},
                    intellsysBaseUrl: intellsysBaseUrl,
                    path: "/geography/countries/get",
                }),
                {method: "GET"},
                countryValidator.array(),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return countriesResult;
}

export async function getTimeZonesWithCaching({
    queryClient,
    intellsysBaseUrl,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    //
}): Promise<Result<Array<TimeZone>>> {
    const timeZonesResult = await queryClient.ensureQueryData({
        queryKey: [TIME_ZONES__QUERY_KEY],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/time-zones/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({}),
                },
                zod.array(
                    zod.object({
                        id: uuidZodValidator,
                        displayName: zod.string(),
                        sdtUtcOffsetInMinutes: integerZodValidator,
                        dstUtcOffsetInMinutes: integerZodValidator,
                    }),
                ),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return timeZonesResult;
}

export async function getPlatformAuthorizationDetailsWithCaching({
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<PlatformAccessMatrix>> {
    const platformAuthorizationDetailsResult = await queryClient.ensureQueryData({
        queryKey: [USER__PLATFORM_ACCESS_MATRIX__QUERY_KEY, accessToken],
        queryFn: async () => {
            const platformAuthorizationDetailsResponse = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/authorization/platform-access/matrix/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({}),
                },
                platformAccessMatrixValidator,
            );
            return platformAuthorizationDetailsResponse;
        },
        retry: false,
        staleTime: Infinity,
    });
    return platformAuthorizationDetailsResult;
}

export async function getPlatformAccessMatrixWithCaching(args: {
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<PlatformAccessMatrix>> {
    return getPlatformAuthorizationDetailsWithCaching(args);
}

export async function getWorkspaceAccessMatrixWithCaching({
    workspaceId,
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    workspaceId: Uuid;
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<WorkspaceAccessMatrix>> {
    return queryClient.ensureQueryData({
        queryKey: [USER__WORKSPACE_ACCESS_MATRIX__QUERY_KEY, workspaceId, accessToken],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/authorization/workspace-access/matrix/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId}),
                },
                workspaceAccessMatrixValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getWorkspaceCapabilityAccessMatrixWithCaching({
    workspaceId,
    accessToken,
    queryClient,
    intellsysBaseUrl,
}: {
    workspaceId: Uuid;
    accessToken: string;
    queryClient: QueryClient;
    intellsysBaseUrl: string;
}): Promise<Result<WorkspaceCapabilityAccessMatrix>> {
    return queryClient.ensureQueryData({
        queryKey: [USER__WORKSPACE_CAPABILITY_ACCESS_MATRIX__QUERY_KEY, workspaceId, accessToken],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/authorization/workspace-capability-access/matrix/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId}),
                },
                workspaceCapabilityAccessMatrixValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getNotifications({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<Notification>>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/notifications/get`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                workspaceId: workspaceId,
            }),
        },
        zod.array(zod.unknown()),
    );
    if (result.success !== true) {
        return result;
    }

    const notifications: Array<Notification> = [];
    for (const item of result.data) {
        const parsed = notificationZodValidator.safeParse(item);
        if (parsed.success === true) {
            notifications.push(parsed.data);
        } else {
            console.error("Skipping invalid notification", parsed.error);
        }
    }
    return dataResult(notifications);
}

export async function updateNotificationStatus({
    notificationId,
    statusId,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
}: {
    notificationId: Uuid;
    statusId: NotificationStatus;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
}): Promise<Result<void>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/notifications/update`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                id: notificationId,
                updates: {
                    status: statusId,
                },
                workspaceId: workspaceId,
            }),
        },
        zod.undefined(),
    );

    return result;
}

export async function getAllWorkspaceUserDetails({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<UserDetails>>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/get-all-platform-user-details`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({}),
        },
        zod.array(userDetailsValidator),
    );

    return result;
}

export async function getAllWorkspaceUserDetailsWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<UserDetails>>> {
    try {
        const result = await queryClient.ensureQueryData({
            queryKey: [WORKSPACE__USERS__PLATFORM_DETAILS__QUERY_KEY, workspaceId],
            queryFn: async () => {
                const userDetailsResult = await getAllWorkspaceUserDetails({
                    workspaceId: workspaceId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: accessToken,
                });
                if (userDetailsResult.success !== true) {
                    throw userDetailsResult.error;
                }
                return userDetailsResult.data;
            },
            retry: false,
            staleTime: Infinity,
        });

        return dataResult(result);
    } catch (error) {
        return errorResult(error instanceof Error ? error : new Error("Failed to get workspace user details", {cause: error}));
    }
}

export async function getAllWorkspaceCombinedUsers({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<Array<CombinedUser>>> {
    const [workspaceUsersResult, platformUsersResult] = await Promise.all([
        getWorkspaceUsers({
            workspaceId: workspaceId,
            intellsysBaseUrl: intellsysBaseUrl,
            accessToken: accessToken,
            ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
        }),
        getAllWorkspaceUserDetails({
            workspaceId: workspaceId,
            intellsysBaseUrl: intellsysBaseUrl,
            accessToken: accessToken,
        }),
    ]);

    if (workspaceUsersResult.success !== true) {
        return errorResult(workspaceUsersResult.error);
    }
    if (platformUsersResult.success !== true) {
        return errorResult(platformUsersResult.error);
    }

    const platformUserByUserId = new Map<Uuid, UserDetails>(
        platformUsersResult.data.map((platformUser) => [platformUser.userId, platformUser]),
    );
    const combinedUsers: Array<CombinedUser> = [];
    for (const user of workspaceUsersResult.data) {
        const platformUser = platformUserByUserId.get(user.userId);
        if (platformUser === undefined) {
            return errorResult(new Error(`No UserDetails found for userId ${user.userId}`));
        }
        combinedUsers.push({
            platformUser: platformUser,
            user: user,
        });
    }

    return dataResult(combinedUsers);
}

export async function getAllWorkspaceCombinedUsersWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<CombinedUser>>> {
    try {
        const result = await queryClient.ensureQueryData({
            queryKey: [WORKSPACE__COMBINED_USERS__QUERY_KEY, workspaceId],
            queryFn: async () => {
                const combinedUsersResult = await getAllWorkspaceCombinedUsers({
                    workspaceId: workspaceId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: accessToken,
                });
                if (combinedUsersResult.success !== true) {
                    throw combinedUsersResult.error;
                }
                return combinedUsersResult.data;
            },
            retry: false,
            staleTime: Infinity,
        });

        return dataResult(result);
    } catch (error) {
        return errorResult(error instanceof Error ? error : new Error("Failed to get combined workspace users", {cause: error}));
    }
}

/** `/users/get-all` may join platform identity fields; preserve them for table display. */
const workspaceUserWithIdentityZodSchema = userZodSchema.extend({
    name: zod.string().nullable().optional(),
    email: zod.string().nullable().optional(),
    phoneNumber: zod.string().nullable().optional(),
});

export async function getWorkspaceUsers({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<User>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/users/get-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        zod.array(workspaceUserWithIdentityZodSchema),
    );
}

export async function getWorkspaceUsersWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<User>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    return await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USERS__QUERY_KEY, workspaceId, "get-all", input],
        queryFn: () =>
            getWorkspaceUsers({
                accessToken: accessToken,
                complexFilters: complexFilters,
                limit: limit,
                offset: offset,
                orderBy: orderBy,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspaceId,
            }),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getWorkspaceUsersCount({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
}): Promise<Result<Integer>> {
    const input: CacheableWorkspaceItemCountAllInput = {complexFilters};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/users/count-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        integerZodValidator,
    );
}

export async function getWorkspaceUsersColumnValuesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    columnId,
    templateId,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    columnId: string;
    templateId: Uuid;
}): Promise<Result<Array<string>>> {
    const result = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USERS__COLUMN_VALUES__QUERY_KEY, workspaceId, columnId, templateId],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/users/column-values`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        column: columnId,
                        templateId: templateId,
                    }),
                },
                zod.array(zod.string()),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return result;
}

export async function getOrganizationalUnits({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<OrganizationalUnit>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/organizational-units/get-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        zod.array(organizationalUnitZodSchema),
    );
}

export async function getOrganizationalUnitsWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<OrganizationalUnit>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    const result = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY, workspaceId, "get-all", input],
        queryFn: () =>
            getOrganizationalUnits({
                accessToken: accessToken,
                complexFilters: complexFilters,
                limit: limit,
                offset: offset,
                orderBy: orderBy,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspaceId,
            }),
        retry: false,
        staleTime: Infinity,
    });

    return result;
}

export async function getOrganizationalUnitsCount({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
}): Promise<Result<Integer>> {
    const input: CacheableWorkspaceItemCountAllInput = {complexFilters};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/organizational-units/count-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        integerZodValidator,
    );
}

export async function getOrganizationalUnitTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<OrganizationalUnitTemplate>>> {
    const organizationalUnitTemplatesResult = await queryClient.ensureQueryData<Result<Array<OrganizationalUnitTemplate>>>({
        queryKey: [WORKSPACE__ORGANIZATION_UNIT_TEMPLATES__QUERY_KEY, workspaceId],
        queryFn: () =>
            robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({
                    input: {},
                    path: "/organizational-unit-templates/get",
                    intellsysBaseUrl: intellsysBaseUrl,
                }),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                },
                zod.array(organizationalUnitTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return organizationalUnitTemplatesResult;
}

export async function getDomainAggregateTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<DomainAggregateTemplate>>> {
    const domainAggregateTemplatesResult = await queryClient.ensureQueryData<Result<Array<DomainAggregateTemplate>>>({
        queryKey: [WORKSPACE__DOMAIN_AGGREGATE_TEMPLATES__QUERY_KEY, workspaceId],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/domain-aggregates/templates/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({}),
                },
                zod.array(domainAggregateTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return domainAggregateTemplatesResult;
}

export async function getUserDefinedObjectTemplatesWithCaching({
    includeArchived = false,
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    includeArchived?: boolean;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<UserDefinedObjectTemplate>>> {
    return await queryClient.ensureQueryData({
        queryKey: includeArchived
            ? [WORKSPACE__USER_DEFINED_OBJECT_TEMPLATES__QUERY_KEY, workspaceId, "includeArchived"]
            : [WORKSPACE__USER_DEFINED_OBJECT_TEMPLATES__QUERY_KEY, workspaceId],
        queryFn: () => robustJsonResultClientFetch(
            buildPlutoCacheableGetUrl({
                input: includeArchived ? {includeArchived: true} : {},
                intellsysBaseUrl: intellsysBaseUrl,
                path: "/user-defined-objects/templates/get",
            }),
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "X-Workspace-Id": workspaceId,
                },
                method: "GET",
            },
            zod.array(userDefinedObjectTemplateValidator),
        ),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getPublicUserDefinedObjectTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
}): Promise<Result<Array<UserDefinedObjectTemplate>>> {
    return await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USER_DEFINED_OBJECT_TEMPLATES__QUERY_KEY, workspaceId, "public"],
        queryFn: () => robustJsonResultClientFetch(
            `${intellsysBaseUrl}/sub-system/pluto/external/user-defined-object-templates/get`,
            {
                body: JSON.stringify({workspaceId}),
                headers: {"Content-Type": "application/json"},
                method: "POST",
            },
            zod.array(userDefinedObjectTemplateValidator),
        ),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getUserDefinedObjectsWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    templateId,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    templateId: Uuid;
}): Promise<Result<Array<UserDefinedObject>>> {
    return await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USER_DEFINED_OBJECTS__QUERY_KEY, workspaceId, templateId],
        queryFn: () => robustJsonResultClientFetch(
            `${intellsysBaseUrl}/sub-system/pluto/external/user-defined-objects/get`,
            {
                body: JSON.stringify({limit: 99999, offset: 0, userDefinedObjectTemplateId: templateId, workspaceId: workspaceId}),
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                method: "POST",
            },
            zod.array(userDefinedObjectValidator),
        ),
        retry: false,
        staleTime: Infinity,
    });
}

export async function getUserTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<UserTemplate>>> {
    const userTemplatesResult = await queryClient.ensureQueryData<Result<Array<UserTemplate>>>({
        queryKey: [WORKSPACE__USER_TEMPLATES__QUERY_KEY, workspaceId],
        queryFn: () =>
            robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({
                    input: {},
                    path: "/user-templates/get",
                    intellsysBaseUrl: intellsysBaseUrl,
                }),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                },
                zod.array(userTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return userTemplatesResult;
}

export async function getProductCatalogueTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<ProductCatalogueTemplate>>> {
    const productCatalogueTemplatesResult = await queryClient.ensureQueryData<Result<Array<ProductCatalogueTemplate>>>({
        queryKey: [WORKSPACE__PRODUCT_CATALOGUE_TEMPLATES__QUERY_KEY, workspaceId],
        queryFn: () =>
            robustJsonResultClientFetch(
                buildPlutoCacheableGetUrl({
                    input: {},
                    path: "/product-catalogue-templates/get",
                    intellsysBaseUrl: intellsysBaseUrl,
                }),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                },
                zod.array(productCatalogueTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return productCatalogueTemplatesResult;
}

export async function getProductCatalogues({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<ProductCatalogue>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/product-catalogues/get-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        zod.array(productCatalogueValidator),
    );
}

export async function getProductCataloguesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    complexFilters,
    limit,
    offset,
    orderBy,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    complexFilters: PlutoComplexFilter;
    limit: number;
    offset: number;
    orderBy: Array<PlutoOrderByItem>;
}): Promise<Result<Array<ProductCatalogue>>> {
    const input: CacheableWorkspaceItemGetAllInput = {complexFilters, limit, offset, orderBy};

    const productCataloguesResult = await queryClient.ensureQueryData<Result<Array<ProductCatalogue>>>({
        queryKey: [WORKSPACE__PRODUCT_CATALOGUES__QUERY_KEY, workspaceId, "get-all", input],
        queryFn: () =>
            getProductCatalogues({
                accessToken: accessToken,
                complexFilters: complexFilters,
                limit: limit,
                offset: offset,
                orderBy: orderBy,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspaceId,
            }),
        retry: false,
        staleTime: Infinity,
    });

    return productCataloguesResult;
}

export async function getProductCataloguesCount({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    complexFilters,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    complexFilters: PlutoComplexFilter;
}): Promise<Result<Integer>> {
    const input: CacheableWorkspaceItemCountAllInput = {complexFilters};

    return await robustJsonResultClientFetch(
        buildPlutoCacheableGetUrl({
            input: input,
            path: "/product-catalogues/count-all",
            intellsysBaseUrl: intellsysBaseUrl,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        integerZodValidator,
    );
}

export async function getProductInventoriesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<ProductInventory>>> {
    const productInventoriesResult = await queryClient.ensureQueryData<Result<Array<ProductInventory>>>({
        queryKey: [WORKSPACE__PRODUCT_INVENTORIES__QUERY_KEY, workspaceId],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/product-inventories/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({}),
                },
                zod.array(productInventoryValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });

    return productInventoriesResult;
}

export async function getWorkspaceCapabilitiesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<WorkspaceCapability<WorkspaceCapabilityType>>>> {
    const enabledWorkspaceProductsResult = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__CAPABILITIES__QUERY_KEY, workspaceId],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/workspace-capabilities/get`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId: workspaceId}),
                },
                workspaceCapabilitiesValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
    if (enabledWorkspaceProductsResult.success === false) {
        return enabledWorkspaceProductsResult;
    }
    // return dataResult(mergeMockedWorkspaceCapabilities(enabledWorkspaceProductsResult.data, workspaceId));
    return enabledWorkspaceProductsResult;
}

export async function getWorkspacePrivateInvitesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    offset,
    limit,
    filterConfiguration,
    sortConfiguration,
    searchConfiguration,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration?: SearchConfiguration;
}): Promise<Result<Array<PrivateInvite>>> {
    const complexFilters = convertOttopilotViewerFiltersToPlutoComplexFilter({
        filterConfiguration: filterConfiguration,
        searchConfiguration: searchConfiguration,
    });
    const orderBy = convertOttopilotViewerSortToPlutoOrderBy(sortConfiguration);

    const enabledWorkspaceProductsResult = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__QUERY_KEY, workspaceId, "with-filters", limit, offset, complexFilters, orderBy],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        complexFilters: complexFilters,
                        offset: offset,
                        limit: limit,
                        orderBy: orderBy,
                    }),
                },
                zod.array(privateInviteValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return enabledWorkspaceProductsResult;
}

export async function getActiveWorkspacePrivateInvitesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<PrivateInvite>>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__QUERY_KEY, workspaceId, "active"],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        filter: {
                            status: PrivateInviteStatus.active,
                        },
                    }),
                },
                zod.array(privateInviteValidator),
            ),
        retry: false,
        staleTime: 30 * 1000,
    });
}

export async function getBlockingWorkspacePrivateInvitesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<PrivateInvite>>> {
    const blockingStatuses = [
        PrivateInviteStatus.active,
        PrivateInviteStatus.pending,
        PrivateInviteStatus.changesRequested,
        PrivateInviteStatus.rejected,
        PrivateInviteStatus.redeemed,
    ];
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__QUERY_KEY, workspaceId, "duplicate-blocking", blockingStatuses],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        complexFilters: {
                            logic: "AND",
                            filters: [
                                {
                                    column: "status",
                                    operator: "in",
                                    value: blockingStatuses,
                                },
                            ],
                        },
                    }),
                },
                zod.array(privateInviteValidator),
            ),
        retry: false,
        staleTime: 30 * 1000,
    });
}

export async function getWorkspacePrivateInvitesEntriesCount({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    filterConfiguration,
    searchConfiguration,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
}): Promise<Result<Integer>> {
    const complexFilters = convertOttopilotViewerFiltersToPlutoComplexFilter({
        filterConfiguration: filterConfiguration,
        searchConfiguration: searchConfiguration,
    });

    const enabledWorkspaceProductsResult = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__COUNT__QUERY_KEY, workspaceId, complexFilters],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/entries-count`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        complexFilters: complexFilters,
                    }),
                },
                integerZodValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
    return enabledWorkspaceProductsResult;
}

export async function getWorkspacePrivateInvitesColumnValues({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    columnId,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    columnId: string;
}): Promise<Result<Array<string>>> {
    const enabledWorkspaceProductsResult = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__COLUMN__VALUES__QUERY_KEY, workspaceId, columnId],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/column-values`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Workspace-Id": workspaceId,
                    },
                    body: JSON.stringify({
                        column: columnId,
                    }),
                },
                zod.array(zod.string()),
            ),
        retry: false,
        staleTime: Infinity,
    });
    return enabledWorkspaceProductsResult;
}

export async function getPrivateInviteWithInviteCode({
    intellsysBaseUrl,
    accessToken,
    queryClient,
    inviteCode,
    id,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    inviteCode: string;
    id: Uuid;
}): Promise<Result<PrivateInviteByInviteCode>> {
    const privateInviteResult = await queryClient.ensureQueryData({
        queryKey: [WORKSPACE__INVITES__DATA__QUERY_KEY, inviteCode],
        queryFn: async () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/private-invites/get-by-invite-code`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        inviteCode: inviteCode,
                        id: id,
                    }),
                },
                privateInviteByInviteCodeValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
    return privateInviteResult;
}
